// src/lib/services/data/smartUpdate.ts
// Atomic state update utility — the ONLY place that writes to domainState.alerts and domainState.workOrders.
// Handles snapshot updates, delta updates, and grouped array rebuilds.

import type { Alert } from '$lib/types/domain/alerts.types';
import type { AlertGroup } from '$lib/types/core/state.types';
import type { AssetFinancialData } from '$lib/types/domain/alerts.types';
import { domainState, buildHiddenAlertIds, isSiteOutage } from '$lib/state/index.svelte';
import { createLogger } from '$lib/utils/logger';
import { browser } from '$app/environment';
import { APP_VERSION } from '$lib/version';
import { validateAlertGroups, validateWorkOrders } from '$lib/types/websocket/schemas';

const log = createLogger('smartUpdate');

const CACHE_KEY = 'amos:alerts:lastKnownGood';
const CACHE_VERSION = 2; // bump when envelope shape changes

/**
 * DataStatus values the backend includes in every DATA_SNAPSHOT payload.
 * Mirrors amos-backend-linux/app/core/websocket/data_quality.py.
 *
 * - 'fresh'              → real live data, safe to mark CONNECTED
 * - 'stale'              → data returned but older than expected
 * - 'site_data_missing'  → alerts present but asset enrichment failed (DB partial)
 * - 'empty_cache'        → backend returned empty because its own cache was empty
 * - 'unavailable'        → backend could not reach the external database at all
 * - 'unknown'            → unclassified
 */
export type DataQualityStatus =
	| 'fresh'
	| 'stale'
	| 'site_data_missing'
	| 'empty_cache'
	| 'unavailable'
	| 'unknown';

/** Any status that is NOT 'fresh' is considered degraded. */
function isDegraded(status: DataQualityStatus): boolean {
	return status !== 'fresh';
}

// Track last processed snapshot timestamp for stale rejection.
// Alerts use a single combined tracker (regular + approved arrive together).
let lastSnapshotTs = '';
// Work orders get their OWN snapshot tracker (only wired for entity='work_orders'
// snapshots, which carry a snapshot_ts; the combined 'all'/REST path supplies
// none and is instead gated by the REST request-epoch in RestFallbackPoller).
let lastWorkOrderSnapshotTs = '';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

interface BackendGroup {
	asset_id: string;
	asset_name: string;
	manager: string;
	alerts: Record<string, unknown>[];
	daily_data?: Record<string, unknown>[];
}

/**
 * Flatten backend grouped alerts into a flat Alert[] array.
 * Copies `manager` from the group onto each alert.
 */
function flattenGroups(groups: BackendGroup[]): Alert[] {
	const flat: Alert[] = [];
	for (const group of groups) {
		if (!group.alerts || !Array.isArray(group.alerts)) continue;
		for (const rawAlert of group.alerts) {
			flat.push({
				...(rawAlert as unknown as Alert),
				manager: (rawAlert as Record<string, unknown>).manager as string ?? group.manager
			});
		}
	}
	return flat;
}

/**
 * Extract financial data (daily_data) from all backend groups.
 * Adds `asset_id` and `asset_name` to each entry.
 */
function extractFinancialData(groups: BackendGroup[]): AssetFinancialData[] {
	const data: AssetFinancialData[] = [];
	for (const group of groups) {
		if (!group.daily_data || !Array.isArray(group.daily_data)) continue;
		for (const entry of group.daily_data) {
			data.push({
				...(entry as unknown as AssetFinancialData),
				asset_id: group.asset_id,
				asset_name: group.asset_name
			});
		}
	}
	return data;
}

/**
 * Build AlertGroup[] from a flat Alert[] array.
 *
 * 1. Merge alerts with same asset_id into one group
 * 2. Filter out alerts hidden by Site Outage overlap
 * 3. Skip empty groups (all alerts hidden)
 * 4. Populate all fields required by the AlertGroup interface
 */
function buildGroups(flatAlerts: Alert[]): AlertGroup[] {
	if (flatAlerts.length === 0) return [];

	// Build hidden alert IDs set (Site Outage + Tracker overlap filtering)
	const hiddenIds = buildHiddenAlertIds(flatAlerts);

	// Group by asset_id
	const byAsset = new Map<string, { alerts: Alert[]; group: BackendGroup | null }>();
	for (const alert of flatAlerts) {
		const key = alert.asset_id;
		let entry = byAsset.get(key);
		if (!entry) {
			entry = { alerts: [], group: null };
			byAsset.set(key, entry);
		}
		entry.alerts.push(alert);
	}

	const result: AlertGroup[] = [];

	for (const [assetId, entry] of byAsset) {
		const allAlerts = entry.alerts;

		// Separate site outages from non-outage alerts
		const siteOutages = allAlerts.filter((a) => isSiteOutage(a.alert_type));
		const nonOutageAlerts = allAlerts.filter(
			(a) => !isSiteOutage(a.alert_type) && !hiddenIds.has(a.alert_flag_id)
		);

		// Skip empty groups (all non-outage alerts hidden by overlap)
		if (nonOutageAlerts.length === 0 && siteOutages.length === 0) continue;

		// Visible alerts = site outages + non-hidden non-outage alerts
		const visibleAlerts = [...siteOutages, ...nonOutageAlerts];

		// Determine representative values from the first alert in the group
		const first = allAlerts[0];
		const assetName = first.asset_name || '';
		const manager = first.manager || '';

		// Determine mixed alert type
		const alertTypes = new Set(visibleAlerts.map((a) => a.alert_type));
		const alertType = alertTypes.size === 1 ? [...alertTypes][0] : 'Mixed';

		result.push({
			id: assetId,
			asset_id: assetId,
			asset_name: assetName,
			asset: { id: assetId, name: assetName },
			alerts: visibleAlerts,
			site_outages: siteOutages,
			non_outage_alerts: nonOutageAlerts,
			count: visibleAlerts.length,
			isGroup: true,
			manager,
			alert_type: alertType
		});
	}

	return result;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Process a full snapshot from the backend (DATA_SNAPSHOT).
 *
 * @param groupedRegular  - Backend grouped regular_alerts array
 * @param groupedApproved - Backend grouped approved_alerts array
 * @param snapshotTs      - ISO timestamp string for stale rejection
 * @param dataStatus      - Backend-reported data quality. When degraded AND
 *                          the snapshot carries no new data, we REFUSE to
 *                          mark the state as initialized. This prevents the
 *                          "0 0 0 0 = success" lie when the database is sick.
 * @returns true if the snapshot was applied (even if degraded), false if rejected
 */
export function smartUpdateAlerts(
	groupedRegular: BackendGroup[],
	groupedApproved: BackendGroup[],
	snapshotTs?: string,
	dataStatus: DataQualityStatus = 'fresh'
): boolean {
	// Stale rejection: skip if incoming snapshot is older than last processed
	if (snapshotTs && lastSnapshotTs && snapshotTs < lastSnapshotTs) {
		log.debug('Stale snapshot rejected', { incoming: snapshotTs, last: lastSnapshotTs });
		return false;
	}

	// Phase 7: runtime shape validation at the data boundary.
	// Invalid groups are dropped with a warning instead of silently
	// corrupting the state. Previously flattenGroups/buildGroups would
	// silently skip malformed groups (missing alerts array, non-string
	// asset_id) — now we log the count and continue with the valid
	// subset. If validation drops EVERYTHING, the smartUpdate refuses
	// to mark initialized=true (handled by the empty+degraded guard).
	const rawRegularCount = Array.isArray(groupedRegular) ? groupedRegular.length : 0;
	const rawApprovedCount = Array.isArray(groupedApproved) ? groupedApproved.length : 0;
	const validatedRegular = validateAlertGroups(
		groupedRegular ?? [],
		(idx, err) => log.warn('Invalid regular alert group dropped', { idx, issues: err.issues })
	) as BackendGroup[];
	const validatedApproved = validateAlertGroups(
		groupedApproved ?? [],
		(idx, err) => log.warn('Invalid approved alert group dropped', { idx, issues: err.issues })
	) as BackendGroup[];
	if (validatedRegular.length !== rawRegularCount) {
		log.warn('Dropped invalid regular alert groups', {
			dropped: rawRegularCount - validatedRegular.length,
			kept: validatedRegular.length,
			total: rawRegularCount
		});
	}
	if (validatedApproved.length !== rawApprovedCount) {
		log.warn('Dropped invalid approved alert groups', {
			dropped: rawApprovedCount - validatedApproved.length,
			kept: validatedApproved.length,
			total: rawApprovedCount
		});
	}

	// Flatten groups into flat arrays (using validated groups only)
	const flatRegular = flattenGroups(validatedRegular);
	const flatApproved = flattenGroups(validatedApproved);

	// Never overwrite non-empty with empty
	const existingRegular = domainState.alerts.items;
	const existingApproved = domainState.alerts.approvedItems;

	const useRegular =
		flatRegular.length > 0 || existingRegular.length === 0 ? flatRegular : existingRegular;
	const useApproved =
		flatApproved.length > 0 || existingApproved.length === 0 ? flatApproved : existingApproved;

	// Extract financial data from both regular and approved groups
	const financialRegular = extractFinancialData(validatedRegular);
	const financialApproved = extractFinancialData(validatedApproved);
	const allFinancial = [...financialRegular, ...financialApproved];

	// Deduplicate financial data by asset_id + timestamp
	const financialMap = new Map<string, AssetFinancialData>();
	for (const entry of allFinancial) {
		const key = `${entry.asset_id}:${entry.timestamp}`;
		financialMap.set(key, entry);
	}
	const newFinancialData = [...financialMap.values()];

	// MERGE rather than replace. A snapshot scoped to a subset of alerts — e.g.
	// the approved-only refresh fired right after a work order is created from an
	// alert — only carries financial data for assets STILL in that feed. A
	// straight replace would drop the just-dispatched asset's entry, so its WO
	// card's "7d rev" badge (which looks the asset up in this cache by asset_id
	// via getAssetFinancialData) would vanish until a full reload.
	//
	// New entries are authoritative for every asset they cover (full replace
	// per asset — no stale accumulation, preserves the per-asset daily-entry
	// structure the 7-day sum depends on); existing entries are RETAINED only
	// for assets the new snapshot does not mention. When the new snapshot is
	// empty this keeps the entire existing cache — preserving the original
	// "don't overwrite non-empty with empty" guard.
	const existingFinancial = domainState.alerts.financialData;
	const newFinancialAssetIds = new Set(newFinancialData.map((e) => String(e.asset_id)));
	const retainedFinancial = existingFinancial.filter(
		(e) => !newFinancialAssetIds.has(String(e.asset_id))
	);
	const financialData = [...newFinancialData, ...retainedFinancial];

	// Build grouped arrays for UI rendering
	const groupedItems = buildGroups(useRegular);
	const groupedApprovedItems = buildGroups(useApproved);

	// CRITICAL: the "empty+degraded" guard.
	//
	// Previously this function unconditionally set `isLoading=false` and
	// `initialized=true`. When the backend DB was sick, it returned
	// `DATA_SNAPSHOT { regular_alerts: [], approved_alerts: [], data_status: 'empty_cache' }`
	// and the frontend treated that as a successful "zero alerts" render —
	// the direct cause of the "0 0 0 0" ghost state the user kept seeing.
	//
	// Now: if the incoming data is BOTH empty AND marked degraded by the
	// backend, we do NOT flip initialized/isLoading. The recovery machine
	// will receive the same data_status through DataSyncService and route
	// the phase to DEGRADED instead of CONNECTED.
	const incomingIsEmpty = useRegular.length === 0 && useApproved.length === 0;
	const hasPriorData = existingRegular.length > 0 || existingApproved.length > 0;
	const backendIsDegraded = isDegraded(dataStatus);
	const refuseToMarkReady = incomingIsEmpty && backendIsDegraded && !hasPriorData;

	// Atomic assignment — arrays always applied (buildGroups handles empty)
	domainState.alerts.items = useRegular;
	domainState.alerts.approvedItems = useApproved;
	domainState.alerts.groupedItems = groupedItems;
	domainState.alerts.groupedApprovedItems = groupedApprovedItems;
	domainState.alerts.financialData = financialData;
	domainState.alerts.dataHealth = dataStatus;
	domainState.alerts.lastUpdateTime = new Date();

	if (refuseToMarkReady) {
		// Keep the loading state engaged so the dashboard shows a skeleton
		// rather than a blank "zero" screen. The recovery machine will
		// surface the DEGRADED phase via the banner, so the user sees an
		// explicit reason instead of silence.
		log.warn('Refusing to mark alerts as initialized: backend returned empty+degraded', {
			dataStatus,
			incomingRegular: useRegular.length,
			incomingApproved: useApproved.length
		});
		// Keep isLoading=true, initialized=false
	} else {
		domainState.alerts.isLoading = false;
		domainState.alerts.initialized = true;
	}

	// Update stale rejection tracker
	if (snapshotTs) {
		lastSnapshotTs = snapshotTs;
	}

	log.debug('Snapshot applied', {
		regular: useRegular.length,
		approved: useApproved.length,
		groupedRegular: groupedItems.length,
		groupedApproved: groupedApprovedItems.length,
		financial: financialData.length,
		dataStatus,
		initialized: !refuseToMarkReady,
		snapshotTs
	});

	// localStorage cache — save only FRESH snapshots. Never cache degraded
	// data, because that's how we ended up in a feedback loop where stale
	// empty caches kept re-poisoning the next session.
	//
	// Envelope format (v2): version-tagged + app-version-tagged + timestamped
	// so future schema changes can reject old caches without hand-holding.
	if (browser && dataStatus === 'fresh') {
		try {
			const envelope = {
				cacheVersion: CACHE_VERSION,
				appVersion: APP_VERSION,
				writtenAt: Date.now(),
				snapshotTs,
				dataStatus,
				// Cache only validated groups — anything malformed has
				// already been dropped by validateAlertGroups above.
				regular_alerts: validatedRegular,
				approved_alerts: validatedApproved
			};
			localStorage.setItem(CACHE_KEY, JSON.stringify(envelope));
		} catch (e) {
			// localStorage may be full or unavailable — non-fatal
			log.debug('Failed to cache snapshot to localStorage', { error: e });
		}
	} else if (browser && backendIsDegraded) {
		log.debug('Skipping cache write — data is degraded', { dataStatus });
	}

	return true;
}

/**
 * Apply a BATCH of alert deltas in a single pass.
 *
 * P9: fixes O(batch × n) behavior where each per-item applyAlertDelta
 * rebuilt both grouped arrays from scratch. For K deltas on an array
 * of N alerts, the old path was effectively O(K·N). The new path is
 * O(N + K): build an index once, mutate in place, rebuild groups once.
 *
 * Callers that receive bulk updates (DATA_DELTA_BATCH messages) should
 * prefer this over a per-item loop. `applyAlertDelta` is kept as a
 * convenience wrapper for single-item calls.
 */
export function applyAlertDeltaBatch(
	deltas: Array<{ action: string; alert: Alert }>
): void {
	if (deltas.length === 0) return;

	// Start from the current state — copy once, not per-item.
	const regularItems = [...domainState.alerts.items];
	const approvedItems = [...domainState.alerts.approvedItems];

	// Build two indexes: alert_flag_id → array index in each list.
	// Rebuilding these per-delta would be O(n·K). Building once makes
	// all lookups O(1) and the whole pass O(n + K).
	const regularIndex = new Map<string, number>();
	for (let i = 0; i < regularItems.length; i++) {
		const id = regularItems[i]?.alert_flag_id;
		if (id) regularIndex.set(id, i);
	}
	const approvedIndex = new Map<string, number>();
	for (let i = 0; i < approvedItems.length; i++) {
		const id = approvedItems[i]?.alert_flag_id;
		if (id) approvedIndex.set(id, i);
	}

	// Track items to remove (lazy deletion — we splice at the end).
	const regularDeletes = new Set<number>();
	const approvedDeletes = new Set<number>();

	for (const { action, alert } of deltas) {
		if (!alert || !alert.alert_flag_id) {
			log.debug('Batch delta skipped — missing alert or alert_flag_id', { action });
			continue;
		}
		const alertFlagId = alert.alert_flag_id;

		// Merge with existing data if this is a partial update.
		const existingRegularIdx = regularIndex.get(alertFlagId);
		const existingApprovedIdx = approvedIndex.get(alertFlagId);
		const existingAlert =
			existingRegularIdx !== undefined && !regularDeletes.has(existingRegularIdx)
				? regularItems[existingRegularIdx]
				: existingApprovedIdx !== undefined && !approvedDeletes.has(existingApprovedIdx)
					? approvedItems[existingApprovedIdx]
					: null;

		const merged: Alert = existingAlert ? { ...existingAlert, ...alert } : alert;
		const status = (merged.status ?? '').toLowerCase();
		const isVerified = merged.is_verified === true;

		// Determine target list
		let target: 'approved' | 'regular' | 'none';
		if (action === 'remove') {
			// Explicit removal (reject / resolve / converted-to-work-order) — drop
			// regardless of last-known status, matching the single-item
			// applyAlertDelta path. Without this a batched remove of an approved
			// alert would be re-filed as approved and kept.
			target = 'none';
		} else if (isVerified || status === 'approved' || status === 'reverted_wo') {
			target = 'approved';
		} else if (
			(status === 'new' || status === 'open' || status === 'reverted_alert') &&
			!isVerified
		) {
			target = 'regular';
		} else if (existingAlert && !alert.status) {
			target = existingApprovedIdx !== undefined ? 'approved' : 'regular';
		} else {
			target = 'none';
		}

		// Remove from both indexes (lazy — will splice at the end)
		if (existingRegularIdx !== undefined) {
			regularDeletes.add(existingRegularIdx);
		}
		if (existingApprovedIdx !== undefined) {
			approvedDeletes.add(existingApprovedIdx);
		}

		// Add to target list (update in place if possible, else push)
		if (target === 'regular') {
			const newIdx = regularItems.length;
			regularItems.push(merged);
			regularIndex.set(alertFlagId, newIdx);
		} else if (target === 'approved') {
			const newIdx = approvedItems.length;
			approvedItems.push(merged);
			approvedIndex.set(alertFlagId, newIdx);
		}
		// target === 'none' → fully removed, no add step
	}

	// Compact the lists — drop all lazily-deleted indexes.
	const finalRegular = regularItems.filter((_, i) => !regularDeletes.has(i));
	const finalApproved = approvedItems.filter((_, i) => !approvedDeletes.has(i));

	// Rebuild grouped arrays ONCE at the end instead of per-delta.
	const groupedItems = buildGroups(finalRegular);
	const groupedApprovedItems = buildGroups(finalApproved);

	// Atomic assignment
	domainState.alerts.items = finalRegular;
	domainState.alerts.approvedItems = finalApproved;
	domainState.alerts.groupedItems = groupedItems;
	domainState.alerts.groupedApprovedItems = groupedApprovedItems;
	domainState.alerts.lastUpdateTime = new Date();

	log.debug('Batch delta applied', {
		deltaCount: deltas.length,
		regular: finalRegular.length,
		approved: finalApproved.length,
		regularDeleted: regularDeletes.size,
		approvedDeleted: approvedDeletes.size
	});
}

/**
 * Apply a single alert delta (approve/reject/resolve action).
 *
 * For bulk updates, prefer `applyAlertDeltaBatch` which is O(N + K)
 * instead of O(N × K). This function is kept for single-item callers.
 *
 * 1. Determine target list based on is_verified / status
 * 2. Remove alert from BOTH flat lists by alert_flag_id
 * 3. Add to correct flat list (or neither if resolved/rejected)
 * 4. Rebuild BOTH grouped arrays atomically
 */
export function applyAlertDelta(action: string, alert: Alert): boolean {
	if (!alert || !alert.alert_flag_id) {
		log.debug('Delta skipped — missing alert or alert_flag_id', { action });
		return false;
	}

	const alertFlagId = alert.alert_flag_id;

	// Merge with existing alert data if this is a partial update
	// Backend sometimes sends only changed fields (e.g. work_order_id: null)
	// We need the full alert (especially status/is_verified) to route correctly
	const existingAlert =
		domainState.alerts.items.find((a) => a.alert_flag_id === alertFlagId) ||
		domainState.alerts.approvedItems.find((a) => a.alert_flag_id === alertFlagId);

	const merged: Alert = existingAlert ? { ...existingAlert, ...alert } : alert;

	const status = (merged.status ?? '').toLowerCase();
	const isVerified = merged.is_verified === true;

	// Determine target: approved list, regular list, or neither (filtered out)
	let target: 'approved' | 'regular' | 'none';

	if (action === 'remove') {
		// Explicit removal (reject / resolve / converted-to-work-order): drop the
		// alert from every list regardless of its last-known status. Without this,
		// the status-based routing below would re-file an 'approved' alert back
		// into the approved list and it would never leave other clients' boards
		// until the next full refresh.
		target = 'none';
	} else if (isVerified || status === 'approved' || status === 'reverted_wo') {
		target = 'approved';
	} else if (
		(status === 'new' || status === 'open' || status === 'reverted_alert') &&
		!isVerified
	) {
		target = 'regular';
	} else if (existingAlert && !alert.status) {
		// Partial update without status — keep alert in its current list
		// This handles work order revert where backend only sends work_order_id: null
		const wasInApproved = domainState.alerts.approvedItems.some(
			(a) => a.alert_flag_id === alertFlagId
		);
		target = wasInApproved ? 'approved' : 'regular';
	} else {
		// resolved, rejected, closed, etc. — remove from both lists
		target = 'none';
	}

	// Remove from BOTH flat lists
	const filteredRegular = domainState.alerts.items.filter(
		(a) => a.alert_flag_id !== alertFlagId
	);
	const filteredApproved = domainState.alerts.approvedItems.filter(
		(a) => a.alert_flag_id !== alertFlagId
	);

	// Add merged alert to target list (merged = existing data + incoming changes)
	if (target === 'regular') {
		filteredRegular.push(merged);
	} else if (target === 'approved') {
		filteredApproved.push(merged);
	}

	// Rebuild both grouped arrays
	const groupedItems = buildGroups(filteredRegular);
	const groupedApprovedItems = buildGroups(filteredApproved);

	// Atomic assignment — all 4 arrays
	domainState.alerts.items = filteredRegular;
	domainState.alerts.approvedItems = filteredApproved;
	domainState.alerts.groupedItems = groupedItems;
	domainState.alerts.groupedApprovedItems = groupedApprovedItems;
	domainState.alerts.lastUpdateTime = new Date();

	log.debug('Delta applied', {
		action,
		alertFlagId,
		target,
		regular: filteredRegular.length,
		approved: filteredApproved.length
	});
	return true;
}

/**
 * Apply a SINGLE work-order delta (add/update/remove). The ONLY place WO delta
 * mutations to domainState.workOrders happen for live ingestion, so the caller
 * can wrap it in the per-WO sequence guard. Field-merges add/update (preserving
 * nested fields like the alerts array on a WO) and drops on remove.
 *
 * @returns true if the state actually changed, false otherwise.
 */
export function applyWorkOrderDelta(action: string, item: Record<string, unknown>): boolean {
	if (!item || item.id == null) {
		log.debug('WO delta skipped — missing id', { action });
		return false;
	}
	const items = domainState.workOrders.items;

	if (action === 'remove') {
		const next = items.filter((wo: Record<string, unknown>) => wo.id !== item.id);
		if (next.length === items.length) return false; // nothing removed
		domainState.workOrders.items = next;
		return true;
	}

	if (action === 'add' || action === 'update') {
		const updated = [...items];
		const idx = updated.findIndex((wo: Record<string, unknown>) => wo.id === item.id);
		if (idx !== -1) {
			updated[idx] = { ...(updated[idx] as Record<string, unknown>), ...item };
		} else {
			updated.push(item);
		}
		domainState.workOrders.items = updated;
		return true;
	}

	log.warn('applyWorkOrderDelta: unknown action', { action });
	return false;
}

/**
 * Process a work order snapshot.
 * Never overwrites non-empty with empty.
 * Respects data_status like smartUpdateAlerts — empty+degraded+no-prior
 * refuses to mark initialized, so dashboards keep the skeleton up and
 * the recovery machine surfaces the DEGRADED banner.
 *
 * @param snapshotTs - optional snapshot timestamp for stale-rejection. Only the
 *   entity='work_orders' path threads this; the combined 'all'/REST path passes
 *   undefined (it is gated by the REST request-epoch instead).
 * @returns true if update was applied, false if skipped
 */
export function smartUpdateWorkOrders(
	items: unknown[],
	// P11: default changed from 'fresh' to 'unknown' so any caller that
	// forgets to thread the backend-reported status through routes the
	// snapshot through the degraded path instead of silently marking
	// the work-orders domain as successfully loaded with empty data.
	dataStatus: DataQualityStatus = 'unknown',
	snapshotTs?: string
): boolean {
	if (!Array.isArray(items)) {
		log.debug('Work orders update skipped — not an array');
		return false;
	}

	// Stale rejection: skip a WO snapshot older than the last processed one.
	// Mirrors the alert snapshot guard. Only active when a snapshot_ts is
	// supplied (entity='work_orders'); the 'all'/REST path passes none.
	if (snapshotTs && lastWorkOrderSnapshotTs && snapshotTs < lastWorkOrderSnapshotTs) {
		log.debug('Stale WO snapshot rejected', { incoming: snapshotTs, last: lastWorkOrderSnapshotTs });
		return false;
	}

	// Phase 7: runtime shape validation. Drop items that don't have an
	// id field (the minimum smartUpdate+tables+deltas need). Previously
	// we cast the whole array as any[] and hoped.
	const rawCount = items.length;
	const validated = validateWorkOrders(
		items,
		(idx, err) => log.warn('Invalid work order dropped', { idx, issues: err.issues })
	);
	if (validated.length !== rawCount) {
		log.warn('Dropped invalid work orders', {
			dropped: rawCount - validated.length,
			kept: validated.length,
			total: rawCount
		});
	}

	// Never overwrite non-empty with empty. BUT: still let the caller
	// notify the recovery machine so a degraded snapshot routes to
	// DEGRADED even when we're keeping the stale rows visible. Returning
	// `false` here would make DataSyncService.handleSnapshot skip
	// notifyRecoverySnapshot and the banner would never appear (codex
	// finding on P11 follow-up). Record the health status and return
	// `true` so the machine learns about it.
	if (validated.length === 0 && domainState.workOrders.items.length > 0) {
		log.debug('Work orders empty overwrite prevented — keeping prior rows, still notifying recovery', {
			dataStatus
		});
		domainState.workOrders.lastUpdateTime = new Date();
		// Don't touch isLoading/initialized — they're already true because
		// we have prior data. The banner comes from the recovery phase,
		// not from domainState.workOrders.dataHealth, so we don't need to
		// set a health field here.
		if (snapshotTs) lastWorkOrderSnapshotTs = snapshotTs;
		return true;
	}

	const incomingIsEmpty = validated.length === 0;
	const hasPriorData = domainState.workOrders.items.length > 0;
	const backendIsDegraded = isDegraded(dataStatus);
	const refuseToMarkReady = incomingIsEmpty && backendIsDegraded && !hasPriorData;

	domainState.workOrders.items = validated as any[];
	domainState.workOrders.lastUpdateTime = new Date();

	if (refuseToMarkReady) {
		log.warn('Refusing to mark work orders as initialized: empty+degraded', { dataStatus });
		// Keep isLoading=true, initialized=false — skeleton stays visible
	} else {
		domainState.workOrders.isLoading = false;
		domainState.workOrders.initialized = true;
	}

	if (snapshotTs) lastWorkOrderSnapshotTs = snapshotTs;

	log.debug('Work orders updated', { count: validated.length, dataStatus, initialized: !refuseToMarkReady });
	return true;
}

/**
 * Reset the snapshot-freshness trackers. Called on logout (clearAll). These use
 * wall-clock snapshot_ts which does NOT rewind on a backend restart, so unlike
 * the delta sequence maps they are NOT reset on reconnect.
 */
export function resetSnapshotTrackers(): void {
	lastSnapshotTs = '';
	lastWorkOrderSnapshotTs = '';
}
